import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normSearch } from "@/lib/utils";
import { PasswordField } from "@/components/Atoms";
import { Plus, MagnifyingGlass, Buildings, Briefcase, Wallet, Calendar } from "@phosphor-icons/react";

const COP = (n) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n || 0);

export default function Clients() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("todos"); // todos | mensual | proyecto | sin_suscripcion
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ nit: "", razon_social: "", direccion: "", telefono: "", email: "", encargado_nombre: "", sector: "", gps_lat: null, gps_lng: null, password: "" });
    const [createdCreds, setCreatedCreds] = useState(null);
    const [error, setError] = useState("");

    const canCreate = user?.role === "comercial" || user?.role === "supervisor";

    const load = async () => {
        const r = await api.get("/clients");
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const captureGps = () => {
        if (!navigator.geolocation) return alert("GPS no disponible");
        navigator.geolocation.getCurrentPosition(
            pos => setForm(f => ({...f, gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude})),
            err => alert("Error GPS: " + err.message),
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    // KPIs agregados por modalidad (memoizados)
    const kpis = useMemo(() => {
        const proyectos = items.filter(c => c.subscription_summary?.modalidad === "proyecto");
        const mensuales = items.filter(c => c.subscription_summary?.modalidad === "mensual");
        const sinSub    = items.filter(c => !c.subscription_summary);
        const presupuestoProyectosTotal = proyectos.reduce((a, c) => a + (c.subscription_summary?.presupuesto_total_cop || 0), 0);
        const mrrMensual = mensuales.reduce((a, c) => a + (c.subscription_summary?.precio_mensual || 0), 0);
        const diasProyPromedio = proyectos.length > 0
            ? Math.round(proyectos.reduce((a, c) => a + (c.subscription_summary?.dias_restantes || 0), 0) / proyectos.length)
            : 0;
        return {
            proyectos: proyectos.length, mensuales: mensuales.length, sinSub: sinSub.length,
            presupuestoProyectosTotal, mrrMensual, diasProyPromedio
        };
    }, [items]);

    const filtered = items.filter(c => {
        // Filtro por modalidad
        const modalidad = c.subscription_summary?.modalidad;
        if (filter === "mensual" && modalidad !== "mensual") return false;
        if (filter === "proyecto" && modalidad !== "proyecto") return false;
        if (filter === "sin_suscripcion" && c.subscription_summary) return false;
        // Filtro por búsqueda
        if (!q) return true;
        const s = normSearch(q);
        return [c.razon_social, c.nit, c.email, c.sector].some(v => normSearch(v).includes(s));
    });

    const create = async (e) => {
        e.preventDefault(); setError("");
        if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
        try {
            const r = await api.post("/clients", form);
            setCreatedCreds({ ...r.data.login_credentials, razon_social: r.data.razon_social, password: form.password });
            setShowForm(false);
            setForm({ nit: "", razon_social: "", direccion: "", telefono: "", email: "", encargado_nombre: "", sector: "", gps_lat: null, gps_lng: null, password: "" });
            load();
        } catch (err) {
            setError(err.response?.data?.detail || "Error al crear cliente");
        }
    };

    const tabs = [
        { code: "todos",           label: "Todos",            count: items.length,          color: "#0F172A" },
        { code: "mensual",         label: "Plan mensual",     count: kpis.mensuales,        color: "#0F766E" },
        { code: "proyecto",        label: "Proyecto puntual", count: kpis.proyectos,        color: "#7C3AED" },
        { code: "sin_suscripcion", label: "Sin suscripción",  count: kpis.sinSub,           color: "#B45309" },
    ];

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="clients-page">
            <div className="flex items-end justify-between mb-6 border-b border-zinc-200 pb-6">
                <div>
                    <div className="overline mb-2">Catálogo · Área Comercial</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">Clientes + Planes</h1>
                </div>
                {canCreate && (
                    <button onClick={() => setShowForm(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-new-client">
                        <Plus size={16} weight="bold" /> {showForm ? "Cancelar" : "Nuevo cliente"}
                    </button>
                )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mb-6" data-testid="clients-kpis">
                <KpiCell icon={<Briefcase size={18} weight="duotone" style={{ color: "#7C3AED" }} />} label="Proyectos activos" value={kpis.proyectos} sub={`${COP(kpis.presupuestoProyectosTotal)} presupuestados`} testid="kpi-proyectos" />
                <KpiCell icon={<Buildings size={18} weight="duotone" style={{ color: "#0F766E" }} />} label="Planes mensuales activos" value={kpis.mensuales} sub={`${COP(kpis.mrrMensual)} MRR`} testid="kpi-mensuales" />
                <KpiCell icon={<Calendar size={18} weight="duotone" style={{ color: "#0EA5E9" }} />} label="Días promedio · proyectos" value={kpis.diasProyPromedio} sub="hasta cierre estimado" testid="kpi-dias-proyecto" />
                <KpiCell icon={<Wallet size={18} weight="duotone" style={{ color: "#B45309" }} />} label="Sin suscripción" value={kpis.sinSub} sub="oportunidades comerciales" testid="kpi-sin-sub" highlight />
            </div>

            {/* Tabs por modalidad */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="modalidad-tabs">
                {tabs.map(t => (
                    <button
                        key={t.code}
                        onClick={() => setFilter(t.code)}
                        className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-all ${filter === t.code ? "text-white" : "text-zinc-700 bg-white hover:bg-zinc-50"}`}
                        style={filter === t.code ? { background: t.color, borderColor: t.color } : { borderColor: "#E4E4E7" }}
                        data-testid={`tab-${t.code}`}
                    >
                        {t.label} <span className="font-mono ml-1">({t.count})</span>
                    </button>
                ))}
            </div>

            {showForm && (
                <form onSubmit={create} className="border border-zinc-200 p-6 bg-zinc-50 mb-8 grid md:grid-cols-2 gap-4" data-testid="new-client-form">
                    <Input label="NIT *" value={form.nit} onChange={v => setForm({...form, nit: v})} testid="cli-nit" required />
                    <Input label="Razón social *" value={form.razon_social} onChange={v => setForm({...form, razon_social: v})} testid="cli-razon" required />
                    <Input label="Dirección *" value={form.direccion} onChange={v => setForm({...form, direccion: v})} testid="cli-dir" required />
                    <Input label="Teléfono *" value={form.telefono} onChange={v => setForm({...form, telefono: v})} testid="cli-tel" required />
                    <Input label="Email *" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testid="cli-email" required />
                    <Input label="Encargado en sitio" value={form.encargado_nombre} onChange={v => setForm({...form, encargado_nombre: v})} testid="cli-enc" />
                    <Input label="Sector" value={form.sector} onChange={v => setForm({...form, sector: v})} testid="cli-sector" />
                    <div className="md:col-span-2 border-t border-zinc-200 pt-4">
                        <div className="overline mb-2">Coordenadas GPS de la sede (para geofencing)</div>
                        <div className="flex flex-wrap items-end gap-3">
                            <div>
                                <label className="text-xs text-zinc-500">Latitud</label>
                                <input type="number" step="any" className="input-atia font-mono w-40" value={form.gps_lat || ""} onChange={e => setForm({...form, gps_lat: parseFloat(e.target.value) || null})} data-testid="cli-lat" />
                            </div>
                            <div>
                                <label className="text-xs text-zinc-500">Longitud</label>
                                <input type="number" step="any" className="input-atia font-mono w-40" value={form.gps_lng || ""} onChange={e => setForm({...form, gps_lng: parseFloat(e.target.value) || null})} data-testid="cli-lng" />
                            </div>
                            <button type="button" onClick={captureGps} className="btn-secondary" data-testid="cli-capture-gps">📍 Usar ubicación actual</button>
                        </div>
                    </div>
                    <div className="md:col-span-2 border-t border-zinc-200 pt-4">
                        <div className="overline mb-2" style={{ color: "#0F766E" }}>🔐 Credenciales del portal del cliente</div>
                        <p className="text-xs text-zinc-600 mb-3">El cliente ingresará al portal con su <strong>NIT</strong> ({form.nit || "—"}) y la contraseña que escribas aquí. Comunícasela en privado.</p>
                        <Input label="Contraseña inicial * (mínimo 6 caracteres)" type="password" value={form.password} onChange={v => setForm({...form, password: v})} testid="cli-password" required />
                    </div>
                    {error && <div className="md:col-span-2 p-3 border border-red-300 bg-red-50 text-red-900 text-sm" data-testid="cli-error">{error}</div>}
                    <div className="md:col-span-2 flex justify-end">
                        <button type="submit" className="btn-primary" data-testid="submit-client">Crear cliente y generar acceso →</button>
                    </div>
                </form>
            )}

            {createdCreds && (
                <div className="border border-emerald-300 bg-emerald-50 p-6 mb-8" data-testid="created-creds">
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>✅ Cliente creado · Credenciales del portal</div>
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{createdCreds.razon_social}</h3>
                    <p className="text-sm text-zinc-700 mb-3">Entrega estos datos al cliente para que ingrese a su portal:</p>
                    <div className="bg-white border border-zinc-200 p-4 font-mono text-sm space-y-1.5">
                        <div><span className="text-zinc-500">Identificador ({createdCreds.identifier_label}):</span> <strong>{createdCreds.identifier}</strong></div>
                        <div><span className="text-zinc-500">Contraseña:</span> <strong>{createdCreds.password}</strong></div>
                        <div><span className="text-zinc-500">Portal:</span> <strong>{window.location.origin}/login</strong></div>
                    </div>
                    <button onClick={() => setCreatedCreds(null)} className="btn-secondary mt-4 text-sm" data-testid="dismiss-creds">Entendido, cerrar</button>
                </div>
            )}

            <div className="relative mb-6 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por razón social, NIT, sector…" className="input-atia pl-10" data-testid="clients-search" />
            </div>

            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="clients-table">
                    <thead><tr><th>Razón social</th><th>NIT</th><th>Modalidad</th><th>Sector</th><th>Encargado</th><th>Teléfono</th><th>Email</th></tr></thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-zinc-500">Sin clientes en este filtro.</td></tr>}
                        {filtered.map(c => {
                            const sub = c.subscription_summary;
                            return (
                                <tr key={c.client_id} className="row-link" onClick={() => (window.location.href = `/clients/${c.client_id}`)} data-testid={`client-row-${c.client_id}`}>
                                    <td className="font-semibold">{c.razon_social}</td>
                                    <td className="font-mono text-xs">{c.nit}</td>
                                    <td>
                                        {!sub ? (
                                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-800" data-testid={`modalidad-${c.client_id}`}>— SIN SUSCRIPCIÓN</span>
                                        ) : sub.modalidad === "proyecto" ? (
                                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-purple-500 bg-purple-50 text-purple-800" data-testid={`modalidad-${c.client_id}`}>PROYECTO PUNTUAL</span>
                                        ) : (
                                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-teal-500 bg-teal-50 text-teal-800" data-testid={`modalidad-${c.client_id}`}>PLAN MENSUAL</span>
                                        )}
                                    </td>
                                    <td>{c.sector || "—"}</td>
                                    <td>{c.encargado_nombre || "—"}</td>
                                    <td className="font-mono text-xs">{c.telefono}</td>
                                    <td className="text-zinc-600 text-xs">{c.email}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, testid, type = "text", required }) {
    return (
        <label>
            <div className="overline mb-1.5">{label}</div>
            {type === "password" ? (
                <PasswordField value={value} onChange={onChange} testid={testid} required={required} autoComplete="new-password" />
            ) : (
                <input type={type} className="input-atia" value={value} onChange={e => onChange(e.target.value)} data-testid={testid} required={required} />
            )}
        </label>
    );
}

function KpiCell({ icon, label, value, sub, testid, highlight }) {
    return (
        <div className={`p-5 border-r border-zinc-200 last:border-r-0 ${highlight ? "bg-zinc-50" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                {icon}
                <div className="overline">{label}</div>
            </div>
            <div className="font-display text-3xl font-black tracking-tighter font-mono">{value}</div>
            {sub && <div className="text-[11px] text-zinc-500 mt-1 font-mono">{sub}</div>}
        </div>
    );
}
